"use client";

import { c as _c } from "react/compiler-runtime";
import { Dialog } from "@base-ui/react/dialog";
import { IconArrowBoxLeft } from "central-icons/IconArrowBoxLeft";
import { IconUser } from "central-icons/IconUser";
import { cn } from "cn";
import { m } from "motion/react";
import Link from "next/link";
import { useState } from "react";
import { useAuth } from "@/components/auth/auth-provider";
import { HomeLink } from "@/components/common/logo";
import { NavLink } from "@/components/shell/nav-link";
import { ThemeToggle } from "@/components/shell/theme-toggle";
import { Button } from "@/components/ui/button";
import { mainNavItems, type NavItem, secondaryNavItems } from "@/data/routes";
import { MD_MEDIA_QUERY, useMediaQuery } from "@/hooks/use-media-query";
import { springTransitions } from "@/lib/animation-presets";
const secondaryLinkClassName = "flex h-10 w-full items-center gap-3 font-medium text-base text-fg-secondary";
export function MobileNavigation() {
  const $ = _c(9);
  const [open, setOpen] = useState(false);
  const isDesktop = useMediaQuery(MD_MEDIA_QUERY);
  if (open && isDesktop) {
    setOpen(false);
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Dialog.Trigger render={<Button aria-label="Open menu" className="rounded-full" size="icon-lg" variant="ghost"><MenuIcon expanded={false} /></Button>} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Dialog.Title className="sr-only">Site navigation</Dialog.Title>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Dialog.Close nativeButton={false} render={<HomeLink />} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex h-(--nav-height) shrink-0 items-center justify-between">{t2}<Dialog.Close render={<Button aria-label="Close menu" className="rounded-full" size="icon-lg" variant="ghost"><MenuIcon expanded={true} /></Button>} /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <nav><ul className="flex flex-col">{mainNavItems.map(_temp)}</ul></nav>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="mt-4 border-gray-300 border-t pt-4"><ul className="flex flex-col">{secondaryNavItems.map(_temp2)}<MobileAuthItem /></ul></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Dialog.Portal><Dialog.Popup className="fixed inset-0 z-(--z-overlay) flex flex-col overflow-y-auto bg-gray-background px-5 pb-6 transition-opacity duration-100 ease-out data-ending-style:opacity-0 data-starting-style:opacity-0">{t1}{t3}{t4}{t5}<div className="mt-4 flex items-center gap-2 border-gray-300 border-t pt-4"><ThemeToggle /></div></Dialog.Popup></Dialog.Portal>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== open) {
    t7 = <Dialog.Root onOpenChange={setOpen} open={open}>{t0}{t6}</Dialog.Root>;
    $[7] = open;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return t7;
}
function _temp2(item_0) {
  return <MobileNavLink className={secondaryLinkClassName} item={item_0} key={item_0.href} />;
}
function _temp(item) {
  return <MobileNavLink className="h-12 gap-3 text-fg text-xl" item={item} key={item.href} />;
}
const lineVariants = {
  collapsed: (position: "top" | "bottom") => ({
    rotate: 0,
    scaleX: 1,
    y: position === "top" ? -4 : 4
  }),
  expanded: (position: "top" | "bottom") => ({
    rotate: position === "top" ? 48 : -48,
    scaleX: 1.1,
    y: 0
  })
};
function MenuIcon(t0) {
  const $ = _c(3);
  const {
    expanded
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["top", "bottom"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== expanded) {
    t2 = <div className="relative flex size-6 flex-col items-center justify-center will-change-transform">{(t1 as const).map(position => <m.div animate={expanded ? "expanded" : "collapsed"} className="absolute h-0.5 w-4 rounded-xs bg-gray-1100" custom={position} initial="collapsed" key={position} transition={springTransitions.fast} variants={lineVariants} />)}</div>;
    $[1] = expanded;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function MobileNavLink(t0) {
  const $ = _c(5);
  const {
    item,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("w-full", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== item || $[3] !== t1) {
    t2 = <li><Dialog.Close nativeButton={false} render={<NavLink className={t1} iconClassName="text-fg-muted" item={item} />} /></li>;
    $[2] = item;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function MobileAuthItem() {
  const $ = _c(4);
  const {
    user,
    isLoading,
    signOut
  } = useAuth();
  if (isLoading) {
    return null;
  }
  if (!user) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <li><Dialog.Close nativeButton={false} render={<Link className={secondaryLinkClassName} href="/sign-in"><IconUser aria-hidden="true" className="size-5 text-fg-muted" mode="raw" />Sign in</Link>} /></li>;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  let t0;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <IconArrowBoxLeft aria-hidden="true" className="size-5" mode="raw" />;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== signOut) {
    t1 = <li><Dialog.Close render={<button className="flex h-10 w-full items-center gap-3 font-medium text-base text-red-500" onClick={signOut} type="button">{t0}Sign out</button>} /></li>;
    $[2] = signOut;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}